import React, { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Dropdown,
  Empty,
  Skeleton,
  Typography,
} from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useAppSelector } from '../../hooks/redux';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { formatCurrencyLocalized } from '../../utils/currencyHelpers';
import { exportCSV, exportExcel, exportPDF, ExportConfig } from '../../utils/reportExport';
import { useGetPnLQuery } from '../../services/financeApi';
import { toIsoDate } from '../../utils/dateHelpers';
import MobileDateRangePicker from '../common/MobileDateRangePickerV2';

const { Text } = Typography;

type QuickRange = 'today' | 'week' | 'month' | 'year' | 'custom';

const RANGE_PILLS: { value: QuickRange; label: string }[] = [
  { value: 'today', label: 'Hoy' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
  { value: 'year', label: 'Año' },
];

// Note on timezones: backend converts `from`/`to` (local yyyy-MM-dd) to UTC using
// the restaurant's TimeZone setting. We compute "today/week/month/year" anchors in
// the restaurant's TZ — NOT the browser's — so support users in different
// timezones get the same buckets the cashier sees on the floor. Then we strip
// down to yyyy-MM-dd via toIsoDate before sending.
//
// `now` here is restaurant-local. `startOfWeek` is the project's Monday-anchored
// helper (dateHelpers.startOfWeekInTz) — dayjs's built-in startOf('week') is
// Sunday-based and would disagree with every other weekly report in the app
// (Codex review on PR-54).
interface RangeAnchors {
  now: Dayjs;
  startOfDay: Dayjs;
  startOfWeek: Dayjs;
  startOfMonth: Dayjs;
  timeZone: string;
}

function resolveRange(
  range: QuickRange,
  anchors: RangeAnchors,
  custom?: [Dayjs, Dayjs] | null,
): [Dayjs, Dayjs] {
  switch (range) {
    case 'today':
      return [anchors.startOfDay, anchors.now];
    case 'week':
      return [anchors.startOfWeek, anchors.now];
    case 'month':
      return [anchors.startOfMonth, anchors.now];
    case 'year':
      return [dayjs().tz(anchors.timeZone).startOf('year'), anchors.now];
    case 'custom':
      return custom && custom[0] && custom[1]
        ? [custom[0], custom[1]]
        : [anchors.startOfMonth, anchors.now];
  }
}

const PnLTab: React.FC = () => {
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const restaurantId = currentRestaurant?.id;
  const { isMobile } = useResponsive();
  const { formatInTimeZone, timeZone, startOfDay, startOfWeek, startOfMonth } =
    useRestaurantTimeZone();

  const [quickRange, setQuickRange] = useState<QuickRange>('month');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);

  const [from, to] = useMemo(
    () => {
      const anchors: RangeAnchors = {
        now: dayjs().tz(timeZone),
        startOfDay: startOfDay(),
        startOfWeek: startOfWeek(),
        startOfMonth: startOfMonth(),
        timeZone,
      };
      return resolveRange(quickRange, anchors, customRange);
    },
    // startOfDay/Week/Month are stable references from the hook; track timeZone
    // and the explicit choice/custom range.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [quickRange, customRange, timeZone]
  );

  const fromStr = toIsoDate(from);
  const toStr = toIsoDate(to);

  const { data, isFetching, isError, refetch } = useGetPnLQuery(
    { restaurantId: restaurantId!, from: fromStr, to: toStr },
    { skip: !restaurantId }
  );

  const periodLabel = useMemo(() => {
    // formatInTimeZone applies the restaurant TZ — the picker is browser-local,
    // but the displayed date should match how the user sees other timestamps
    // throughout the app (per CLAUDE.md timezone convention).
    if (from.isSame(to, 'day')) {
      return formatInTimeZone(from.toISOString(), 'DD MMM YYYY');
    }
    return `${formatInTimeZone(from.toISOString(), 'DD MMM')} – ${formatInTimeZone(to.toISOString(), 'DD MMM YYYY')}`;
  }, [from, to, formatInTimeZone]);

  if (!restaurantId) {
    return <Alert type="warning" message="No hay restaurante seleccionado." />;
  }

  const money = (n: number) => formatCurrencyLocalized(n, 'MXN');

  const profitIsPositive = (data?.operatingProfit ?? 0) >= 0;
  const expensePct =
    data && data.sales.net > 0 ? (data.expenses.total / data.sales.net) * 100 : null;
  const marginPct =
    data && data.sales.net > 0 ? (data.operatingProfit / data.sales.net) * 100 : null;
  const maxCategory = data
    ? Math.max(...data.expenses.byCategory.map((c) => c.amount), 1)
    : 1;
  const maxSalesMethod = data
    ? Math.max(...data.sales.byPaymentMethod.map((pm) => pm.amount), 1)
    : 1;
  const maxExpenseMethod = data
    ? Math.max(...data.expenses.byPaymentMethod.map((pm) => pm.amount), 1)
    : 1;

  // ----- Export -----
  const handleExport = (fmt: 'csv' | 'excel' | 'pdf') => {
    if (!data) return;

    const fmtPct = (n: number) => (n > 0 ? `${n.toFixed(1)}%` : '—');

    const config: ExportConfig = {
      reportTitle: 'Estado de Resultados',
      periodLabel,
      restaurantName: currentRestaurant?.name || 'N/A',
      timestamp: formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm'),
      summaryCards: [
        { label: 'Ventas netas', value: money(data.sales.net) },
        { label: 'Total gastos', value: money(data.expenses.total) },
        { label: 'Utilidad operativa', value: money(data.operatingProfit) },
      ],
      sections: [
        {
          title: 'Ventas',
          titleStyle: 'default',
          headers: ['Concepto', 'Monto'],
          rows: [
            ['Ventas brutas', money(data.sales.gross)],
            ['Descuentos', money(data.sales.discounts)],
            ['Ventas netas', money(data.sales.net)],
          ],
        },
        {
          title: 'Gastos por categoría',
          titleStyle: 'section',
          headers: ['Categoría', 'Monto'],
          rows: [
            ...data.expenses.byCategory.map((c) => [c.name, money(c.amount)]),
            ['Total gastos', money(data.expenses.total)],
          ],
        },
        {
          title: 'Ventas por método de pago',
          titleStyle: 'cash',
          headers: ['Método', 'Monto', '%'],
          rows: data.sales.byPaymentMethod.map((pm) => [
            pm.displayName || pm.method,
            money(pm.amount),
            fmtPct(pm.percentage),
          ]),
        },
        {
          title: 'Gastos por método de pago',
          titleStyle: 'cashier',
          headers: ['Método', 'Monto', '%'],
          rows: data.expenses.byPaymentMethod.map((pm) => [
            pm.name,
            money(pm.amount),
            fmtPct(pm.percentage),
          ]),
        },
      ],
    };

    if (fmt === 'csv') exportCSV(config, 'estado-resultados');
    else if (fmt === 'excel') exportExcel(config, 'estado-resultados');
    else exportPDF(config);
  };

  const exportMenuItems = [
    { key: 'csv', label: 'CSV', onClick: () => handleExport('csv') },
    { key: 'excel', label: 'Excel', onClick: () => handleExport('excel') },
    { key: 'pdf', label: 'PDF', onClick: () => handleExport('pdf') },
  ];

  return (
    <div>
      {/* Período como pills + rango custom + exportar, una sola fila */}
      <div className="fin-filters">
        <div className="fin-pills">
          {RANGE_PILLS.map((p) => (
            <button
              key={p.value}
              type="button"
              className={`fin-pill${quickRange === p.value ? ' fin-pill--selected' : ''}`}
              onClick={() => setQuickRange(p.value)}
            >
              {p.label}
            </button>
          ))}
          <button
            type="button"
            className={`fin-pill${quickRange === 'custom' ? ' fin-pill--selected' : ''}`}
            onClick={() => setQuickRange('custom')}
          >
            Personalizado
          </button>
        </div>
        {quickRange === 'custom' && (
          <MobileDateRangePicker
            value={customRange}
            onChange={(v) => setCustomRange(v && v[0] && v[1] ? [v[0], v[1]] : null)}
            format="DD/MM/YYYY"
            allowClear={false}
            style={{ width: isMobile ? '100%' : 260 }}
          />
        )}
        <div style={{ flex: 1 }} />
        <Dropdown menu={{ items: exportMenuItems }} disabled={!data}>
          <Button icon={<DownloadOutlined />}>{!isMobile && 'Exportar'}</Button>
        </Dropdown>
      </div>

      {/* Error state */}
      {isError && (
        <Alert
          type="error"
          showIcon
          message="No se pudo cargar el Estado de Resultados."
          action={
            <Button size="small" onClick={() => refetch()}>
              Reintentar
            </Button>
          }
          style={{ marginBottom: 14 }}
        />
      )}

      {/* Loading state */}
      {isFetching && !data && (
        <div className="fin-panel fin-panel--pad">
          <Skeleton active paragraph={{ rows: 8 }} />
        </div>
      )}

      {data && (
        <>
          {/* KPIs: el resultado se lee sin scroll */}
          <div className="fin-kpis fin-kpis--3">
            <div className="fin-kpi">
              <div className="fin-kpi__label">Ventas netas</div>
              <div className="fin-kpi__value">{money(data.sales.net)}</div>
              <div className="fin-kpi__sub">
                brutas {money(data.sales.gross)}
                {data.sales.discounts > 0 && ` − desc. ${money(data.sales.discounts)}`}
              </div>
            </div>
            <div className="fin-kpi">
              <div className="fin-kpi__label">Gastos</div>
              <div className="fin-kpi__value">{money(data.expenses.total)}</div>
              <div className="fin-kpi__sub">
                {expensePct !== null ? `${expensePct.toFixed(1)}% de las ventas` : ' '}
              </div>
            </div>
            <div className="fin-kpi">
              <div className="fin-kpi__label">Utilidad operativa</div>
              <div
                className="fin-kpi__value"
                style={{ color: profitIsPositive ? 'var(--color-success-text)' : 'var(--color-error-text)' }}
              >
                {money(data.operatingProfit)}
              </div>
              <div className="fin-kpi__sub">
                {marginPct !== null ? `margen ${marginPct.toFixed(1)}%` : ' '}
              </div>
            </div>
          </div>

          {/* Un solo estado de resultados: ventas → gastos con barras → utilidad hero */}
          <div className="fin-panel fin-panel--pad">
            <div className="fin-panel__head" style={{ border: 0, marginBottom: 0, paddingTop: 0 }}>
              <span className="fin-panel__title">Estado de resultados</span>
              <span className="fin-panel__meta">{periodLabel}</span>
            </div>
            <div className="fin-pnl-line">
              <span>Ventas brutas</span>
              <span className="fin-money">{money(data.sales.gross)}</span>
            </div>
            <div className="fin-pnl-line fin-pnl-line--indent">
              <span>Descuentos</span>
              <span className="fin-money" style={{ color: 'var(--color-error-text)' }}>
                −{money(data.sales.discounts)}
              </span>
            </div>
            <div className="fin-pnl-line fin-pnl-line--sub">
              <span>Ventas netas</span>
              <span className="fin-money">{money(data.sales.net)}</span>
            </div>

            {data.expenses.byCategory.length === 0 ? (
              <div style={{ padding: '10px 0' }}>
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description="Sin gastos registrados en este periodo"
                />
              </div>
            ) : (
              <div style={{ padding: '8px 0 2px' }}>
                {data.expenses.byCategory.map((c) => (
                  <div key={c.categoryId} className="fin-hrow">
                    <span>{c.name}</span>
                    <span className="fin-hrow__bar">
                      <i style={{ width: `${(c.amount / maxCategory) * 100}%` }} />
                    </span>
                    <span className="fin-hrow__amount">−{money(c.amount)}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="fin-pnl-line fin-pnl-line--sub">
              <span>Total gastos</span>
              <span className="fin-money" style={{ color: 'var(--color-error-text)' }}>
                −{money(data.expenses.total)}
              </span>
            </div>

            <div className={`fin-pnl-hero ${profitIsPositive ? 'fin-pnl-hero--pos' : 'fin-pnl-hero--neg'}`}>
              <div>
                <Text strong>Utilidad operativa</Text>
                <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                  ventas netas − gastos
                </div>
              </div>
              <span
                className="fin-money"
                style={{
                  fontSize: 22,
                  color: profitIsPositive ? 'var(--color-success-text)' : 'var(--color-error-text)',
                }}
              >
                {money(data.operatingProfit)}
              </span>
            </div>
          </div>

          {/* Costos de inventario: informativos, no restan en la utilidad operativa
              porque las compras a proveedores pueden estar ya registradas como gastos */}
          {data.inventoryCosts && data.inventoryCosts.costOfGoodsSold > 0 && (
            <div className="fin-panel fin-panel--pad" style={{ marginTop: 12 }}>
              <div className="fin-panel__head">
                <span className="fin-panel__title">Costo de insumos (informativo)</span>
                <span className="fin-panel__meta">
                  food cost {data.inventoryCosts.foodCostPercentage.toFixed(1)}%
                </span>
              </div>
              <div className="fin-pnl-line">
                <span>Costo de lo vendido (insumos)</span>
                <span className="fin-money">−{money(data.inventoryCosts.costOfGoodsSold)}</span>
              </div>
              {data.inventoryCosts.wasteCost > 0 && (
                <div className="fin-pnl-line fin-pnl-line--indent">
                  <span>Mermas</span>
                  <span className="fin-money" style={{ color: 'var(--color-error-text)' }}>
                    −{money(data.inventoryCosts.wasteCost)}
                  </span>
                </div>
              )}
              {data.inventoryCosts.internalConsumptionCost > 0 && (
                <div className="fin-pnl-line fin-pnl-line--indent">
                  <span>Consumo interno</span>
                  <span className="fin-money">−{money(data.inventoryCosts.internalConsumptionCost)}</span>
                </div>
              )}
              <div className="fin-pnl-line fin-pnl-line--sub">
                <span>Utilidad bruta (ventas netas − costo de lo vendido)</span>
                <span className="fin-money">{money(data.inventoryCosts.grossProfit)}</span>
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 6 }}>
                No se resta en la utilidad operativa para no duplicar compras ya registradas como gastos.
              </div>
            </div>
          )}

          {/* Desgloses por método como barras de proporción, lado a lado */}
          {(data.sales.byPaymentMethod.length > 0 || data.expenses.byPaymentMethod.length > 0) && (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
                gap: 12,
                marginTop: 12,
              }}
            >
              {data.sales.byPaymentMethod.length > 0 && (
                <div className="fin-panel fin-panel--pad">
                  <div className="fin-panel__head">
                    <span className="fin-panel__title">Ventas por método</span>
                  </div>
                  {data.sales.byPaymentMethod.map((pm) => (
                    <div key={pm.method} className="fin-hrow">
                      <span>{pm.displayName || pm.method}</span>
                      <span className="fin-hrow__bar">
                        <i style={{ width: `${(pm.amount / maxSalesMethod) * 100}%` }} />
                      </span>
                      <span className="fin-hrow__amount">
                        {money(pm.amount)}
                        {pm.percentage > 0 && <small>{pm.percentage.toFixed(0)}%</small>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {data.expenses.byPaymentMethod.length > 0 && (
                <div className="fin-panel fin-panel--pad">
                  <div className="fin-panel__head">
                    <span className="fin-panel__title">Gastos por método</span>
                  </div>
                  {data.expenses.byPaymentMethod.map((pm) => {
                    // "Sin método" es la señal accionable: gasto sin clasificar → barra ámbar
                    const isNoMethod = !pm.paymentMethodId;
                    return (
                      <div key={pm.paymentMethodId || 'none'} className="fin-hrow">
                        <span>{pm.name}</span>
                        <span className={`fin-hrow__bar${isNoMethod ? ' fin-hrow__bar--warn' : ''}`}>
                          <i style={{ width: `${(pm.amount / maxExpenseMethod) * 100}%` }} />
                        </span>
                        <span className="fin-hrow__amount">
                          {money(pm.amount)}
                          {pm.percentage > 0 && <small>{pm.percentage.toFixed(0)}%</small>}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <div style={{ marginTop: 10, fontSize: 12, color: 'var(--text-secondary)' }}>
            Vista administrativa para gestión — no es un reporte fiscal.
            {data.period.timeZone && ` · Zona horaria: ${data.period.timeZone}`}
          </div>
        </>
      )}
    </div>
  );
};

export default PnLTab;
